import React, { useCallback } from 'react';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import { NavLink } from 'react-router-dom';
import { Icon } from '@modulor/react';
import Local from '../../Local/Local';
import Skeleton from '../../Skeleton/Skeleton';
import NavigationBlock from '../NavigationBlock/NavigationBlock';
import { LinkWithAnalytics } from '../../Link/Link';
import Image from '../../Image/Image';
import withAnalytics from '../../../hocs/withAnalytics';
import { useAnalyticsScreenName } from '../../../hooks/useAnalyticsScreenName';
import { useIsDesktopLayout } from '../../../hooks/useIsDesktopLayout';
import { providerColouredLogoImageSelector } from '../../../redux/reducers/app/configuration';
import {
  getProvidersRoute,
  getProviderDetailRoute,
} from '../../../utils/routes';
import { getDataTestAttrs } from '../../../utils/testing-locators';
import { getProviderColouredLogoImagePath } from '../../../utils/imagePath';
import { EVENT_ACTION } from '../../../constants/ga';
import { NAMESPACES } from '../../../constants/namespaces';
import {
  NAVIGATION_PROVIDER_LINK,
  NAVIGATION_PROVIDERS,
  NAVIGATION_PROVIDERS_LINK,
  NAVIGATION_PROVIDERS_LINK_ICON,
  NAVIGATION_PROVIDERS_LINK_ARROW,
  NAVIGATION_PROVIDER_LINK_LOGO,
  NAVIGATION_PROVIDER_LINK_TITLE,
  NAVIGATION_PROVIDER_LINK_GAME_COUNT,
} from '../../../constants/dataTestIds';
import {
  ANALYTICS_SCHEMA_URLS,
  ANALYTICS_SCREEN_AREAS,
  ANALYTICS_SCREEN_AREA_DETAILS,
} from '../../../constants/analytics';
import { useProviders } from './hooks';
import styles from './NavigationBlockProviders.module.css';

export const NavLinkWithAnalytics = withAnalytics(NavLink);

const NavigationBlockProviders = ({ className }) => {
  const isDesktop = useIsDesktopLayout();
  const imagePath = useSelector(providerColouredLogoImageSelector);
  const screenName = useAnalyticsScreenName();

  const { query, collection } = useProviders();

  const renderItem = useCallback(
    ({ id, translationKey, gameCount }, index) =>
      query.isLoading ? (
        <div className={styles.providerItem}>
          <Skeleton className={styles.skeleton} />
        </div>
      ) : (
        <NavLinkWithAnalytics
          dataLayerEventData={{
            eventAction: EVENT_ACTION.NAV_PROVIDER_ITEM,
            section_index: 3,
            position_index: index,
            provider_id: id,
          }}
          analyticsData={{
            schema: ANALYTICS_SCHEMA_URLS.MORE_GAME_CLICK,
            screen_name: screenName,
            screen_area: ANALYTICS_SCREEN_AREAS.SIDEBAR,
            screen_area_detail: ANALYTICS_SCREEN_AREA_DETAILS.PROVIDER,
            provider_id: id,
            section_index: 3,
            position_index: index,
          }}
          to={getProviderDetailRoute(id)}
          className={(isActive) =>
            classNames(styles.providerItem, isActive && styles.active)
          }
          {...getDataTestAttrs(NAVIGATION_PROVIDER_LINK, id)}
        >
          <Image
            src={getProviderColouredLogoImagePath(imagePath, id)}
            className={styles.providerLogo}
            alt={id}
            fallback={
              <Icon
                name="no_icon"
                className={styles.providerLogo}
                {...getDataTestAttrs(NAVIGATION_PROVIDER_LINK_LOGO)}
              />
            }
            {...getDataTestAttrs(NAVIGATION_PROVIDER_LINK_LOGO)}
          />
          <span
            className={styles.providerTitle}
            {...getDataTestAttrs(NAVIGATION_PROVIDER_LINK_TITLE)}
          >
            {translationKey}
          </span>
          <span
            className={styles.gameCount}
            {...getDataTestAttrs(NAVIGATION_PROVIDER_LINK_GAME_COUNT)}
          >
            {gameCount}
          </span>
          <Icon name="chevron_right" className={styles.providerArrow} />
        </NavLinkWithAnalytics>
      ),
    [imagePath, query.isLoading, screenName]
  );

  return (
    <NavigationBlock
      collection={collection}
      title={
        <LinkWithAnalytics
          to={getProvidersRoute()}
          dataLayerEventData={{
            eventAction: EVENT_ACTION.NAV_PROVIDERS,
            section_index: 3,
          }}
          analyticsData={{
            schema: ANALYTICS_SCHEMA_URLS.ALL_CLASSIFICATION_CLICK,
            screen_name: screenName,
            screen_area: ANALYTICS_SCREEN_AREAS.SIDEBAR,
            screen_area_detail: ANALYTICS_SCREEN_AREA_DETAILS.ALL_PROVIDER,
            section_index: 3,
          }}
          className={styles.title}
          {...getDataTestAttrs(NAVIGATION_PROVIDERS_LINK)}
        >
          {!isDesktop && (
            <Icon
              name="copy_outlined"
              className={styles.icon}
              size={20}
              {...getDataTestAttrs(NAVIGATION_PROVIDERS_LINK_ICON)}
            />
          )}
          <Local namespace={NAMESPACES.PAGES}>Providers</Local>
          <span className={styles.all}>
            <Local namespace={NAMESPACES.INITIAL}>Show all</Local>
          </span>
          <Icon
            name="chevron_right"
            className={styles.arrow}
            {...getDataTestAttrs(NAVIGATION_PROVIDERS_LINK_ARROW)}
          />
        </LinkWithAnalytics>
      }
      renderItem={renderItem}
      className={classNames(className, styles.providerList)}
      {...getDataTestAttrs(NAVIGATION_PROVIDERS)}
    />
  );
};

export default NavigationBlockProviders;
